import {
    DefaultButton,
    Dialog, DialogFooter,
    DialogType, Dropdown, IDropdownOption
} from "@fluentui/react";

import React from "react";
import {Css} from "nano-css/addon/vcssom/cssToTree";
import CompactColorPicker from "../Inputs/CompactColorPicker";
import NumberField from "../Inputs/NumberField";
import ToggleField from '../Common/ToggleField';
import Accordion from "../Common/Accordion";
import { DynamicSetting } from "../Common/Setting";

export interface IStorefrontSectionEditorModalProps {
    css?: Css;
    displayModal: boolean;
    closer: () => void;
    onChange?: (key: string, value: any) => void;
}

/**
 * Marks a control with the CSS property it writes, so documentation and support can link to it.
 *
 * The id is built from the property rather than registered, because that name is already the stable
 * identifier and it covers the granular border variants without a hand-maintained list. The link
 * has to name a section as well as a storefront, so it is one copied from the highlight while a
 * section's editor is open rather than a generic `/go/setting/` link.
 */
export const StyleField: React.FC<{ prop: string; editor?: 'style' | 'layout' }> = ({
    prop,
    editor = 'style',
    children,
}) => <DynamicSetting id={`storefront.section.${editor}.${prop}`}>{children}</DynamicSetting>;
const StorefrontSectionStyleEditorModal: React.FC<IStorefrontSectionEditorModalProps> = props => {
    const [granularBorder, setGranularBorder] = React.useState(false);
    
    const borderStyles: IDropdownOption[] = [
        { key: 'none', text: 'None' },
        { key: 'hidden', text: 'Hidden' },
        { key: 'dotted', text: 'Dotted' },
        { key: 'dashed', text: 'Dashed' },
        { key: 'solid', text: 'Solid' },
        { key: 'double', text: 'Double' },
        { key: 'groove', text: 'Groove' },
        { key: 'ridge', text: 'Ridge' },
        { key: 'inset', text: 'Inset' },
        { key: 'outset', text: 'Outset' },
        { key: 'unset', text: 'Unset' }
    ];
    return (
        <Dialog
            hidden={!props.displayModal}
            onDismiss={props.closer}
            dialogContentProps={{
                type: DialogType.largeHeader,
                title: 'Modify Style',
            }}
            modalProps={{
                isBlocking: true,
            }}
        >
            {props.css &&
            <div>
                <StyleField prop='backgroundColor'>
                    <CompactColorPicker label="Background" color={props.css.backgroundColor ?? '#FFF'} onChange={val => props.onChange?.('backgroundColor', val)}/>
                </StyleField>
                <ToggleField inline={true} label="Detailed Border Controls" checked={granularBorder} onChange={(e, v) => setGranularBorder(!!v)} />
                {!granularBorder ? (
                    <React.Fragment>
                        <StyleField prop='borderColor'>
                            <CompactColorPicker label="Border Color" color={props.css.borderColor ?? '#FFF'} onChange={val => props.onChange?.('borderColor', val)} />
                        </StyleField>
                        <StyleField prop='borderWidth'>
                            <NumberField label="Border Width" value={props.css.borderWidth || 0} onChange={val => props.onChange?.('borderWidth', val)} />
                        </StyleField>
                        <StyleField prop='borderRadius'>
                            <NumberField label="Border Radius" value={props.css.borderRadius || 0} onChange={val => props.onChange?.('borderRadius', val)} />
                        </StyleField>
                        <StyleField prop='borderStyle'>
                            <Dropdown label="Border Style" options={borderStyles} selectedKey={props.css.borderStyle || ''} onChange={(e, val) => props.onChange?.('borderStyle', val?.key)} />
                        </StyleField>
                    </React.Fragment>
                ) : (
                    <React.Fragment>
                        <Accordion beginExpanded={true} useChevron={true} title={`Border Top`}>
                            <div style={{marginBottom: 8, paddingLeft: '1em'}}>
                                <StyleField prop='borderTopColor'>
                                    <CompactColorPicker label={`Color`} color={props.css?.borderTopColor ?? '#FFF'} onChange={val => props.onChange?.(`borderTopColor`, val)} />
                                </StyleField>
                                <StyleField prop='borderTopWidth'>
                                    <NumberField label={`Width`} value={props.css?.borderTopWidth || 0} onChange={val => props.onChange?.(`borderTopWidth`, val)} />
                                </StyleField>
                                <StyleField prop='borderTopRightRadius'>
                                    <NumberField label={`Right Radius`} value={props.css?.borderTopRightRadius || 0} onChange={val => props.onChange?.(`borderTopRightRadius`, val)} />
                                </StyleField>
                                <StyleField prop='borderTopLeftRadius'>
                                    <NumberField label={`Left Radius`} value={props.css?.borderTopLeftRadius || 0} onChange={val => props.onChange?.(`borderTopLeftRadius`, val)} />
                                </StyleField>
                                <StyleField prop='borderTopStyle'>
                                    <Dropdown label={`Style`} options={borderStyles} selectedKey={props.css?.borderTopStyle || ''} onChange={(e, val) => props.onChange?.(`borderTopStyle`, val?.key)} />
                                </StyleField>
                            </div>
                        </Accordion>
                        <Accordion beginExpanded={true} useChevron={true} title={`Border Bottom`}>
                            <div style={{marginBottom: 8, paddingLeft: '1em'}}>
                                <StyleField prop='borderBottomColor'>
                                    <CompactColorPicker label={`Color`} color={props.css?.borderBottomColor ?? '#FFF'} onChange={val => props.onChange?.(`borderBottomColor`, val)} />
                                </StyleField>
                                <StyleField prop='borderBottomWidth'>
                                    <NumberField label={`Width`} value={props.css?.borderBottomWidth || 0} onChange={val => props.onChange?.(`borderBottomWidth`, val)} />
                                </StyleField>
                                <StyleField prop='borderBottomRightRadius'>
                                    <NumberField label={`Right Radius`} value={props.css?.borderBottomRightRadius || 0} onChange={val => props.onChange?.(`borderBottomRightRadius`, val)} />
                                </StyleField>
                                <StyleField prop='borderBottomLeftRadius'>
                                    <NumberField label={`Left Radius`} value={props.css?.borderBottomLeftRadius || 0} onChange={val => props.onChange?.(`borderBottomLeftRadius`, val)} />
                                </StyleField>
                                <StyleField prop='borderBottomStyle'>
                                    <Dropdown label={`Style`} options={borderStyles} selectedKey={props.css?.borderBottomStyle || ''} onChange={(e, val) => props.onChange?.(`borderBottomStyle`, val?.key)} />
                                </StyleField>
                            </div>
                        </Accordion>
                        <Accordion beginExpanded={true} useChevron={true} title={`Border Left`}>
                            <div style={{marginBottom: 8, paddingLeft: '1em'}}>
                                <StyleField prop='borderLeftColor'>
                                    <CompactColorPicker label={`Color`} color={props.css?.borderLeftColor ?? '#FFF'} onChange={val => props.onChange?.(`borderLeftColor`, val)} />
                                </StyleField>
                                <StyleField prop='borderLeftWidth'>
                                    <NumberField label={`Width`} value={props.css?.borderLeftWidth || 0} onChange={val => props.onChange?.(`borderLeftWidth`, val)} />
                                </StyleField>
                                <StyleField prop='borderLeftStyle'>
                                    <Dropdown label={`Style`} options={borderStyles} selectedKey={props.css?.borderLeftStyle || ''} onChange={(e, val) => props.onChange?.(`borderLeftStyle`, val?.key)} />
                                </StyleField>
                            </div>
                        </Accordion>

                    </React.Fragment>
                )}
            </div>
            }
            <DialogFooter>
                <DefaultButton
                    text='Ok'
                    onClick={props.closer}
                />
            </DialogFooter>
        </Dialog>
    );
}
export default StorefrontSectionStyleEditorModal;